export type AudioControlFailScope = 'element' | 'block' | 'questionnaire';

export type AudioControlFailListItem = {
    id?: number | null;
    orderId: number;
    failItem: string;
};

export type QualityControlFailListApiItem = {
    id?: number | null;
    orderId: number;
    failItem: string;
};

export type AudioControlCondition = {
    id?: number | string | null;
    condition: string | null;
    scenario: string | null;
    weight: number | null;
    failCondition: string | null;
    failScope: AudioControlFailScope;
    isChecked?: boolean;
    isActive?: boolean;
};

export type AudioControlBlock = {
    id?: number | string | null;
    name: string | null;
    weight: number | null;
    comment: string | null;
    failCondition: string | null;
    isChecked?: boolean;
    isActive?: boolean;
    conditions: AudioControlCondition[];
};

export type QualityControlMetadata = {
    qualityControlId?: number | null;
    createdByUserId?: string | null;
    createdDateTime?: string | null;
    updatedByUserId?: string | null;
    updatedDateTime?: string | null;
    userCreate?: string | null;
    userUpdate?: string | null;
};

export type AudioControlConditionFieldErrors = {
    condition?: boolean;
    scenario?: boolean;
    weight?: boolean;
    failCondition?: boolean;
};

export type AudioControlBlockFieldErrors = {
    name?: boolean;
    weight?: boolean;
    conditions?: Record<number, AudioControlConditionFieldErrors>;
};

export type AudioControlErrorFields = {
    name?: { name?: string };
    branchId?: { branchId?: string };
    nameUniq?: string;
    blocks?: { blocks?: string };
    questionnaire?: { questionnaire?: string };
    failList?: { failList?: string };
    blockErrors?: Record<number, AudioControlBlockFieldErrors>;
} | null;

export type AudioControlData = {
    id: number | null;
    name: string | null;
    description: string | null;
    branchId: number | null;
    mainBranchId: number | null;
    isActive: boolean;
    isArchived: boolean;
    failList: AudioControlFailListItem[];
    blocks: AudioControlBlock[];
    qualityControlData: QualityControlMetadata | null;
};

export type QualityControlBlockType = 'Block' | 'Element' | 'Questionnaire';

export type QualityControlApiNode = {
    id?: number | null;
    parentId?: number | null;
    qualityControlId?: number | null;
    orderId?: number | null;
    blockType?: QualityControlBlockType | null;
    name?: string | null;
    scenario?: string | null;
    weight?: number | null;
    failText?: string | null;
    isChecked?: boolean | null;
    isActive?: boolean | null;
    children?: QualityControlApiNode[];
};

export const createEmptyFailListItem = (orderId: number = 1): AudioControlFailListItem => ({
    id: null,
    orderId,
    failItem: '',
});

export const createEmptyAudioControlCondition = (): AudioControlCondition => ({
    condition: null,
    scenario: null,
    weight: null,
    failCondition: null,
    failScope: 'element',
    isChecked: true,
    isActive: true,
});

export const createEmptyAudioControlBlock = (): AudioControlBlock => ({
    name: null,
    weight: null,
    comment: null,
    failCondition: null,
    isChecked: true,
    isActive: true,
    conditions: [createEmptyAudioControlCondition()],
});

export const isAudioControlBlockCounted = (block: AudioControlBlock): boolean => block.isActive !== false;

export const isAudioControlBlockActive = (block: AudioControlBlock): boolean => block.isActive !== false;

export const isAudioControlBlockChecked = (block: AudioControlBlock): boolean => block.isChecked !== false;

export const isAudioControlConditionCounted = (condition: AudioControlCondition): boolean => condition.isActive !== false;

export const isAudioControlConditionActive = (condition: AudioControlCondition): boolean => condition.isActive !== false;

export const isAudioControlConditionChecked = (condition: AudioControlCondition): boolean => condition.isChecked !== false;

export const applyInactiveAudioControlCondition = (condition: AudioControlCondition): AudioControlCondition => ({
    ...condition,
    isActive: false,
    weight: 0,
});

export const applyInactiveAudioControlBlock = (block: AudioControlBlock): AudioControlBlock => ({
    ...block,
    isActive: false,
    weight: 0,
    conditions: (block.conditions ?? []).map(applyInactiveAudioControlCondition),
});

export const AUDIO_CONTROL_FAIL_SCOPE_OPTIONS: { value: AudioControlFailScope; label: string }[] = [
    {value: 'element', label: 'Елемент'},
    {value: 'block', label: 'Блок'},
    {value: 'questionnaire', label: 'Анкета'},
];

const normalizeIsActive = (value: unknown): boolean => {
    if (typeof value === 'boolean') {
        return value;
    }
    if (value == null) {
        return true;
    }
    return Boolean(value);
};

const normalizeIsChecked = (value: unknown): boolean => {
    if (typeof value === 'boolean') {
        return value;
    }
    if (value == null) {
        return true;
    }
    return Boolean(value);
};

const getOrderId = (row: QualityControlApiNode): number => Number(row.orderId) || 0;

const sortByOrderId = (items: QualityControlApiNode[]): QualityControlApiNode[] =>
    [...items].sort((leftItem, rightItem) => getOrderId(leftItem) - getOrderId(rightItem));

const mapFailScopeFromBlockType = (blockType: unknown): AudioControlFailScope => {
    const normalizedBlockType = String(blockType ?? '').toLowerCase();
    if (normalizedBlockType === 'questionnaire') {
        return 'questionnaire';
    }
    if (normalizedBlockType === 'block') {
        return 'block';
    }
    return 'element';
};

const mapFailScopeToBlockType = (failScope: AudioControlFailScope): QualityControlBlockType => {
    if (failScope === 'questionnaire') {
        return 'Questionnaire';
    }
    if (failScope === 'block') {
        return 'Block';
    }
    return 'Element';
};

const pickApiId = (value: unknown): number | undefined => {
    if (value == null || value === '') {
        return undefined;
    }

    const parsed = Number(value);
    if (!Number.isInteger(parsed)) {
        return undefined;
    }

    return parsed;
};

const serializeApiWeight = (
    weight: number | null | undefined,
    isCounted: boolean,
): number | null => {
    if (!isCounted) {
        return 0;
    }

    if (weight == null) {
        return null;
    }

    return Number(weight) || 0;
};

const isConditionActiveForApi = (
    condition: AudioControlCondition,
    isBlockActive: boolean,
): boolean => isBlockActive && condition.isActive !== false;

const normalizeApiCondition = (rawCondition: QualityControlApiNode): AudioControlCondition => ({
    id: rawCondition.id ?? null,
    condition: rawCondition.name ?? null,
    scenario: rawCondition.scenario ?? null,
    weight: normalizeIsActive(rawCondition.isActive) ? (rawCondition.weight ?? null) : 0,
    failCondition: rawCondition.failText ?? null,
    failScope: mapFailScopeFromBlockType(rawCondition.blockType),
    isChecked: normalizeIsChecked(rawCondition.isChecked),
    isActive: normalizeIsActive(rawCondition.isActive),
});

const normalizeApiBlock = (rawBlock: QualityControlApiNode): AudioControlBlock => {
    const isBlockActive = normalizeIsActive(rawBlock.isActive);
    const block: AudioControlBlock = {
        id: rawBlock.id ?? null,
        name: rawBlock.name ?? null,
        weight: isBlockActive ? (rawBlock.weight ?? null) : 0,
        comment: rawBlock.scenario ?? null,
        failCondition: rawBlock.failText ?? null,
        isChecked: normalizeIsChecked(rawBlock.isChecked),
        isActive: isBlockActive,
        conditions: sortByOrderId(rawBlock.children ?? []).map(normalizeApiCondition),
    };

    return isBlockActive ? block : applyInactiveAudioControlBlock(block);
};

export const normalizeAudioControlBlocks = (blocks: unknown): AudioControlBlock[] => {
    if (!Array.isArray(blocks)) {
        return [];
    }

    return sortByOrderId(blocks as QualityControlApiNode[]).map(normalizeApiBlock);
};

export const serializeAudioControlConditionToApi = (
    condition: AudioControlCondition,
    orderId: number,
    qualityControlId?: number | null,
    parentId?: number | string | null,
    isBlockActive: boolean = true,
): QualityControlApiNode => {
    const isConditionActive = isConditionActiveForApi(condition, isBlockActive);
    const node: QualityControlApiNode = {
        orderId,
        blockType: mapFailScopeToBlockType(condition.failScope),
        name: condition.condition,
        scenario: condition.scenario,
        weight: serializeApiWeight(condition.weight, isConditionActive),
        failText: condition.failCondition,
        isChecked: condition.isChecked !== false,
        isActive: isConditionActive,
        children: [],
    };

    const id = pickApiId(condition.id);
    if (id != null) {
        node.id = id;
    }

    const resolvedParentId = pickApiId(parentId);
    if (resolvedParentId != null) {
        node.parentId = resolvedParentId;
    }

    const resolvedQualityControlId = pickApiId(qualityControlId);
    if (resolvedQualityControlId != null) {
        node.qualityControlId = resolvedQualityControlId;
    }

    return node;
};

export const serializeAudioControlBlockToApi = (
    block: AudioControlBlock,
    orderId: number,
    qualityControlId?: number | null,
): QualityControlApiNode => {
    const blockId = pickApiId(block.id);
    const isBlockActive = block.isActive !== false;
    const node: QualityControlApiNode = {
        orderId,
        blockType: 'Block',
        name: block.name,
        scenario: block.comment,
        weight: serializeApiWeight(block.weight, isBlockActive),
        failText: block.failCondition,
        isChecked: block.isChecked !== false,
        isActive: isBlockActive,
        children: (block.conditions ?? []).map((condition, conditionIndex) => serializeAudioControlConditionToApi(
            condition,
            conditionIndex + 1,
            qualityControlId,
            blockId ?? block.id,
            isBlockActive,
        )),
    };

    if (blockId != null) {
        node.id = blockId;
    }

    const resolvedQualityControlId = pickApiId(qualityControlId);
    if (resolvedQualityControlId != null) {
        node.qualityControlId = resolvedQualityControlId;
    }

    return node;
};

export const serializeAudioControlBlocksToApi = (
    blocks: AudioControlBlock[],
    qualityControlId?: number | null,
): QualityControlApiNode[] =>
    blocks.map((block, blockIndex) => serializeAudioControlBlockToApi(block, blockIndex + 1, qualityControlId));

export type QualityControlApiPayload = {
    id?: number;
    name: string | null;
    description: string | null;
    branchId: number | null;
    mainBranchId?: number | null;
    isActive: boolean;
    isArchived?: boolean;
    failList: QualityControlFailListApiItem[];
    blocks: QualityControlApiNode[];
};

export const normalizeFailList = (value: unknown): AudioControlFailListItem[] => {
    if (Array.isArray(value) && value.length > 0 && typeof value[0] === 'string') {
        const legacyRows = value.map((failItem, index) => ({
            id: null,
            orderId: index + 1,
            failItem: failItem == null ? '' : String(failItem).trim(),
        }));
        while (legacyRows.length > 1 && legacyRows[legacyRows.length - 1].failItem === '') {
            legacyRows.pop();
        }
        return legacyRows.length ? legacyRows : [createEmptyFailListItem()];
    }

    if (!Array.isArray(value) || value.length === 0) {
        return [createEmptyFailListItem()];
    }

    const normalizedRows = value.map((item, index) => {
        const row = (item ?? {}) as Record<string, unknown>;
        const failItem = String(row.failItem ?? row.FailItem ?? '').trim();
        const orderId = Number(row.orderId ?? row.OrderId ?? index + 1);
        const rawId = row.id ?? row.Id;
        const parsedId = rawId == null || rawId === '' ? null : Number(rawId);

        return {
            id: parsedId === 0 || !Number.isFinite(parsedId) ? null : parsedId,
            orderId: Number.isFinite(orderId) ? orderId : index + 1,
            failItem,
        };
    }).sort((leftItem, rightItem) => leftItem.orderId - rightItem.orderId);

    while (normalizedRows.length > 1 && normalizedRows[normalizedRows.length - 1].failItem === '') {
        normalizedRows.pop();
    }

    return normalizedRows.length ? normalizedRows : [createEmptyFailListItem()];
};

export const serializeFailListToApi = (failList: AudioControlFailListItem[]): QualityControlFailListApiItem[] =>
    failList
        .filter((item) => item.failItem.trim().length > 0)
        .map((item, index) => {
            const node: QualityControlFailListApiItem = {
                orderId: index + 1,
                failItem: item.failItem.trim(),
            };
            const id = pickApiId(item.id);
            if (id != null && id > 0) {
                node.id = id;
            }
            return node;
        });

export const getFilledFailListItems = (failList: AudioControlFailListItem[]): AudioControlFailListItem[] =>
    failList.filter((item) => item.failItem.trim().length > 0);

export const hasFilledFailListItem = (failList: AudioControlFailListItem[] | null | undefined): boolean =>
    getFilledFailListItems(failList ?? []).length > 0;

export const getFailListTexts = (failList: AudioControlFailListItem[]): string[] =>
    getFilledFailListItems(failList).map((item) => item.failItem);

export const serializeAudioControlDataToApi = (data: AudioControlData): QualityControlApiPayload => {
    const payload: QualityControlApiPayload = {
        name: data.name,
        description: data.description,
        branchId: data.branchId,
        mainBranchId: data.mainBranchId,
        isActive: data.isActive,
        isArchived: data.isArchived,
        failList: serializeFailListToApi(data.failList),
        blocks: serializeAudioControlBlocksToApi(data.blocks, data.id),
    };

    const id = pickApiId(data.id);
    if (id != null) {
        payload.id = id;
    }

    return payload;
};

export const normalizeQualityControlMetadata = (value: unknown): QualityControlMetadata | null => {
    if (value == null || typeof value !== 'object' || Array.isArray(value)) {
        return null;
    }

    const row = value as Record<string, unknown>;

    return {
        qualityControlId: (row.qualityControlId ?? null) as number | null,
        createdByUserId: (row.createdByUserId ?? null) as string | null,
        createdDateTime: (row.createdDateTime ?? null) as string | null,
        updatedByUserId: (row.updatedByUserId ?? null) as string | null,
        updatedDateTime: (row.updatedDateTime ?? null) as string | null,
        userCreate: (row.userCreate ?? null) as string | null,
        userUpdate: (row.userUpdate ?? null) as string | null,
    };
};

export const normalizeQuestionnaireFailConditions = (value: unknown): string[] =>
    getFailListTexts(normalizeFailList(value));

export const sumWeights = (items: { weight: number | null | undefined }[]): number =>
    items.reduce((totalWeight, weightedItem) => totalWeight + (Number(weightedItem.weight) || 0), 0);

const WEIGHT_DECIMAL_PLACES = 1;

export const createEqualWeightDistribution = (count: number): number[] => {
    if (count <= 0) {
        return [];
    }

    const factor = 10 ** WEIGHT_DECIMAL_PLACES;
    const totalUnits = 100 * factor;
    const baseUnits = Math.floor(totalUnits / count);
    const remainderUnits = totalUnits - baseUnits * count;

    return Array.from({ length: count }, (_, index) => {
        const units = baseUnits + (index < remainderUnits ? 1 : 0);
        return units / factor;
    });
};

export const distributeEqualBlockWeights = (blocks: AudioControlBlock[]): AudioControlBlock[] => {
    const countedBlockIndexes = blocks.reduce<number[]>((countedIndexes, block, blockIndex) => {
        if (isAudioControlBlockCounted(block)) {
            countedIndexes.push(blockIndex);
        }
        return countedIndexes;
    }, []);

    if (countedBlockIndexes.length === 0) {
        return blocks;
    }

    const equalWeights = createEqualWeightDistribution(countedBlockIndexes.length);
    const weightByBlockIndex = new Map(
        countedBlockIndexes.map((blockIndex, weightIndex) => [blockIndex, equalWeights[weightIndex]])
    );

    return blocks.map((block, blockIndex) => ({
        ...block,
        weight: weightByBlockIndex.get(blockIndex) ?? 0,
    }));
};

export const distributeEqualConditionWeights = (conditions: AudioControlCondition[]): AudioControlCondition[] => {
    const countedConditionIndexes = conditions.reduce<number[]>((countedIndexes, condition, conditionIndex) => {
        if (isAudioControlConditionCounted(condition)) {
            countedIndexes.push(conditionIndex);
        }
        return countedIndexes;
    }, []);

    if (countedConditionIndexes.length === 0) {
        return conditions;
    }

    const equalWeights = createEqualWeightDistribution(countedConditionIndexes.length);
    const weightByConditionIndex = new Map(
        countedConditionIndexes.map((conditionIndex, weightIndex) => [conditionIndex, equalWeights[weightIndex]])
    );

    return conditions.map((condition, conditionIndex) => ({
        ...condition,
        weight: weightByConditionIndex.get(conditionIndex) ?? 0,
    }));
};
